/**
 * FooterBottom — copyright and policy links bar.
 *
 * The link row is CMS-managed (WP Admin → Design System → Footer →
 * Legal → "Footer Bottom Links"), so an editor can enter any kind of
 * URL. `isSpaRoute` therefore decides per-link whether to use a
 * client-side `<Link>` or a plain `<a>`:
 *
 *   - `<Link>` only for real in-app routes (e.g. `/privacy`)
 *   - `<a>`    for absolute URLs, protocol-relative URLs, `mailto:`,
 *              `tel:`, bare hashes, and static files served by the web
 *              server rather than React Router (e.g. `/sitemap.xml`)
 *
 * That last case is why this check exists at all: `/sitemap.xml` is
 * served directly by the server, so routing it through `<Link>` made
 * React Router swallow it and render the 404 page instead.
 */

import { Link } from 'react-router-dom';
import { resetCookieConsent } from '@/lib/cookieConsent';

interface FooterBottomProps {
  copyright: string;
  policies: { label: string; url: string }[];
}

/** File extensions that are served as static assets, not SPA routes. */
const STATIC_FILE_RE = /\.(xml|txt|pdf|json|ico|zip|csv)$/i;

function isSpaRoute(url: string): boolean {
  if (!url.startsWith('/')) return false;      // absolute, mailto:, tel:, #hash, relative
  if (url.startsWith('//')) return false;      // protocol-relative → external
  if (STATIC_FILE_RE.test(url.split(/[?#]/)[0])) return false; // static file
  return true;
}

export function FooterBottom({ copyright, policies }: FooterBottomProps): JSX.Element {
  return (
    <div className="border-t border-slate-200 dark:border-slate-700 pt-6 mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {copyright.replace(/\{year\}/g, new Date().getFullYear().toString())}
      </p>
      <nav aria-label="Legal" className="flex flex-wrap items-center gap-4">
        {policies.map((policy) => {
          const className =
            'text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200';

          if (isSpaRoute(policy.url)) {
            return (
              <Link key={`${policy.label}-${policy.url}`} to={policy.url} className={className}>
                {policy.label}
              </Link>
            );
          }

          const isExternal = /^https?:\/\//i.test(policy.url) || policy.url.startsWith('//');
          return (
            <a
              key={`${policy.label}-${policy.url}`}
              href={policy.url}
              className={className}
              {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              {policy.label}
            </a>
          );
        })}
        <button
          type="button"
          onClick={() => resetCookieConsent()}
          className="text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
        >
          Cookie Settings
        </button>
      </nav>
    </div>
  );
}
